import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import SignaturePad from '../components/SignaturePad';
import api from '../api/axios';

const Profile = () => {
    const { user, updateUser } = useAuth();
    const { config } = useConfig();

    const primaryColor = config?.primaryColor || '#1e40af';

    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editData, setEditData] = useState({
        nombre_1: user?.nombre_1 || user?.name || '',
        nombre_2: user?.nombre_2 || '',
        apellido_1: user?.apellido_1 || user?.last_name || '',
        apellido_2: user?.apellido_2 || '',
        signature: user?.signature || null
    });
    const [error, setError] = useState('');

    const handleSave = async () => {
        setIsSaving(true);
        setError('');
        try {
            const res = await api.put('/users/profile/update', editData);
            updateUser(res.data.user);
            setIsEditing(false);
        } catch (err) {
            console.error("Error updating profile", err);
            setError('Error al actualizar el perfil');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="animate-fadeIn pb-32">
            <header className="mb-10 flex flex-col items-center text-center pt-8 relative">
                <div
                    className="w-24 h-24 rounded-[40px] flex items-center justify-center text-white text-4xl font-black mb-6 shadow-2xl relative"
                    style={{ backgroundColor: primaryColor }}
                >
                    {(user?.nombre_1 || user?.name)?.[0]?.toUpperCase()}
                    <div className="absolute -bottom-2 -right-2 bg-white p-2 rounded-full shadow-lg text-lg">✨</div>
                </div>
                {!isEditing ? (
                    <>
                        <h1 className="text-3xl font-black text-white tracking-tight">{(user?.nombre_1 || user?.name)} {(user?.apellido_1 || user?.last_name)}</h1>
                        <p className="text-white/40 font-bold uppercase tracking-widest text-[10px] mt-2">{user?.role} </p>
                    </>
                ) : (
                    <div className="grid grid-cols-2 gap-3 w-full max-w-sm mt-4">
                        <input
                            className="w-full bg-white/5 border border-white/10 p-3 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/50 text-white font-bold shadow-inner text-xs"
                            placeholder="Primer Nombre"
                            value={editData.nombre_1}
                            onChange={e => setEditData({ ...editData, nombre_1: e.target.value })}
                        />
                        <input
                            className="w-full bg-white/5 border border-white/10 p-3 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/50 text-white font-bold shadow-inner text-xs"
                            placeholder="Segundo Nombre"
                            value={editData.nombre_2}
                            onChange={e => setEditData({ ...editData, nombre_2: e.target.value })}
                        />
                        <input
                            className="w-full bg-white/5 border border-white/10 p-3 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/50 text-white font-bold shadow-inner text-xs"
                            placeholder="Primer Apellido"
                            value={editData.apellido_1}
                            onChange={e => setEditData({ ...editData, apellido_1: e.target.value })}
                        />
                        <input
                            className="w-full bg-white/5 border border-white/10 p-3 rounded-2xl focus:outline-none focus:ring-2 focus:ring-purple-500/50 text-white font-bold shadow-inner text-xs"
                            placeholder="Segundo Apellido"
                            value={editData.apellido_2}
                            onChange={e => setEditData({ ...editData, apellido_2: e.target.value })}
                        />
                    </div>
                )}
            </header>

            <div className="bg-white/10 backdrop-blur-2xl rounded-[40px] p-10 border border-white/20 shadow-2xl space-y-10 relative overflow-hidden max-w-2xl mx-auto">
                <div className="absolute top-4 right-4 z-20">
                    {!isEditing ? (
                        <button
                            onClick={() => setIsEditing(true)}
                            className="bg-white/10 hover:bg-white/20 text-white p-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all backdrop-blur-md"
                        >
                            ✏️ Editar
                        </button>
                    ) : (
                        <div className="flex gap-2">
                            <button
                                onClick={() => {
                                    setIsEditing(false);
                                    setEditData({ 
                                        nombre_1: user?.nombre_1 || user?.name || '', 
                                        nombre_2: user?.nombre_2 || '', 
                                        apellido_1: user?.apellido_1 || user?.last_name || '', 
                                        apellido_2: user?.apellido_2 || '', 
                                        signature: user?.signature
                                    });
                                    setError('');
                                }}
                                className="bg-red-500/20 hover:bg-red-500/40 text-red-100 p-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all backdrop-blur-md"
                            >
                                ✕
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                className="bg-emerald-500/20 hover:bg-emerald-500/40 text-emerald-100 p-3 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all backdrop-blur-md disabled:opacity-50"
                            >
                                {isSaving ? '...' : '💾 GUARDAR'}
                            </button>
                        </div>
                    )}
                </div>
                {error && <p className="text-red-400 text-xs font-bold text-center absolute top-4 left-0 right-0">{error}</p>}
                <div className="absolute -top-10 -left-10 w-40 h-40 bg-purple-500 rounded-full blur-[100px] opacity-20"></div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 relative z-10">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-white/40 pl-1">Correo Electrónico</label>
                        <p className="text-lg font-bold text-white bg-white/5 p-4 rounded-2xl border border-white/10 shadow-inner">{user?.email}</p>
                    </div>
                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-white/40 pl-1">Identificación</label>
                        <p className="text-lg font-bold text-white bg-white/5 p-4 rounded-2xl border border-white/10 shadow-inner">{user?.identification || 'N/A'}</p>
                    </div>
                    <div className="space-y-2 md:col-span-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-white/40 pl-1">Sala Asignada</label>
                        <p className="text-lg font-bold text-white bg-white/5 p-4 rounded-2xl border border-white/10 shadow-inner">{user?.sala_name || 'Sin sala asignada'}</p>
                    </div>
                </div>

                <div className="pt-10 border-t border-white/10 flex flex-col items-center gap-6 relative z-10">
                    <p className="text-xs text-white/60 font-black uppercase tracking-widest text-center leading-relaxed">Pase de Acceso <br />Virtual ID</p>
                    <div className="w-64 bg-white p-6 rounded-[35px] shadow-[0_0_50px_rgba(157,78,221,0.2)] flex flex-col items-center gap-4 relative overflow-hidden group">
                        <div className="absolute inset-0 bg-gradient-to-tr from-purple-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-1000"></div>
                        <div className="relative z-10 p-2 bg-white rounded-2xl">
                            {user?.id && (
                                <QRCodeSVG
                                    value={user.id.toString()}
                                    size={180}
                                    fgColor="#1c0042"
                                    level="H"
                                />
                            )}
                        </div>
                        {isEditing ? (
                            <div className="w-full mt-4 flex flex-col items-center border-t border-slate-100 pt-4 relative z-10 space-y-4">
                                <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">Modificar Firma</p>
                                <div className="w-full bg-slate-50 border border-slate-200 rounded-3xl p-2">
                                    <SignaturePad onSignatureChange={(sig) => setEditData({ ...editData, signature: sig })} />
                                </div>
                                {editData.signature && (
                                    <img src={editData.signature} alt="Firma Nueva" className="max-h-16 object-contain" />
                                )}
                            </div>
                        ) : (
                            user?.signature && (
                                <div className="w-full mt-4 flex flex-col items-center border-t border-slate-100 pt-4 relative z-10">
                                    <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest mb-2">Firma Registrada</p>
                                    <img src={user.signature} alt="Firma del asistente" className="max-h-16 object-contain" />
                                </div>
                            )
                        )}
                        {!isEditing && <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-2 relative z-10">Muestra este código para ingresar</p>}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;
